import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Stack, Typography, Skeleton, Alert, AlertTitle, Button, Paper, IconButton
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import {
  Session, SessionClass, PromotionRun, PromotionRunStatusResponse
} from '../types/sessions';
import { sessionsApi } from '../services/sessions/sessionsApi';
import { classesApi } from '../services/sessions/classesApi';
import { promotionsApi } from '../services/sessions/promotionsApi';

import PromotionSetupStep from '../components/promotions/PromotionSetupStep';
import RunDashboardHeader from '../components/promotions/RunDashboardHeader';
import ClassStatusList from '../components/promotions/ClassStatusList';
import DecisionsTable from '../components/promotions/DecisionsTable';
import FinalizeRunDialog from '../components/promotions/FinalizeRunDialog';

type Mode = 'loading' | 'setup' | 'dashboard' | 'closed';

const PromotionWizardPage: React.FC = () => {
  const { runId: runIdParam } = useParams<{ runId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // The page handles two routes:
  //   /app/promotions/new?sourceSessionId=...   → setup step
  //   /app/promotions/:runId                    → dashboard for that run
  const sourceSessionIdParam = searchParams.get('sourceSessionId');
  const isNewRun = runIdParam === 'new';

  const [mode, setMode] = useState<Mode>('loading');
  const [sourceSession, setSourceSession] = useState<Session | null>(null);
  const [targetSession, setTargetSession] = useState<Session | null>(null);
  const [run, setRun] = useState<PromotionRun | null>(null);
  const [status, setStatus] = useState<PromotionRunStatusResponse | null>(null);
  const [classes, setClasses] = useState<SessionClass[]>([]);

  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [finalizeOpen, setFinalizeOpen] = useState(false);

  // ── Bootstrap: figure out mode + load all the data ───────────────────
  const bootstrap = useCallback(async () => {
    setMode('loading');
    try {
      // Always need the class catalog.
      const allClasses = await classesApi.list();
      setClasses(allClasses);

      if (isNewRun) {
        // Setup: need source session
        if (!sourceSessionIdParam) {
          enqueueSnackbar('No source session specified', { variant: 'error' });
          navigate('/app/settings/sessions');
          return;
        }
        const src = await sessionsApi.get(sourceSessionIdParam);
        setSourceSession(src);

        // Check if a draft for this source already exists → resume it
        const existing = await promotionsApi.listForSession(sourceSessionIdParam);
        const open = existing.find(r => r.status === 'draft' || r.status === 'in_progress');
        if (open) {
          // Hop straight to its dashboard
          navigate(`/app/promotions/${open._id}`, { replace: true });
          return;
        }
        setMode('setup');
      } else if (runIdParam) {
        // Dashboard for an existing run.
        const fetchedStatus = await promotionsApi.getStatus(runIdParam);
        setStatus(fetchedStatus);
        setRun(fetchedStatus.run);

        const [src, tgt] = await Promise.all([
          sessionsApi.get(String(fetchedStatus.run.sourceSessionId)),
          sessionsApi.get(String(fetchedStatus.run.targetSessionId))
        ]);
        setSourceSession(src);
        setTargetSession(tgt);

        // Default-select the first class with anything to do.
        const firstWithWork = fetchedStatus.perClass.find((p: { totalDecided: number; totalUndecided: number; totalCommitted: number }) =>
          (p.totalDecided + p.totalUndecided + p.totalCommitted) > 0
        );
        if (firstWithWork) setSelectedClassId(String(firstWithWork.classId));

        setMode(fetchedStatus.run.status === 'committed' || fetchedStatus.run.status === 'cancelled' ? 'closed' : 'dashboard');
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not load promotion run', { variant: 'error' });
    }
  }, [runIdParam, isNewRun, sourceSessionIdParam, navigate, enqueueSnackbar]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // Refresh the run status (counts, per-class grid) without reloading classes/sessions.
  const refreshStatus = useCallback(async () => {
    if (!runIdParam || runIdParam === 'new') return;
    try {
      const fetched = await promotionsApi.getStatus(runIdParam);
      setStatus(fetched);
      setRun(fetched.run);
      if (fetched.run.status === 'committed' || fetched.run.status === 'cancelled') {
        setMode('closed');
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not refresh status', { variant: 'error' });
    }
  }, [runIdParam, enqueueSnackbar]);

  const handleRunStarted = (newRunId: string) => {
    navigate(`/app/promotions/${newRunId}`, { replace: true });
  };

  const handleFinalized = async () => {
    await refreshStatus();
    enqueueSnackbar('Run finalized — view in Sessions for the audit trail', { variant: 'success' });
  };

  // ── Computed totals across all per-class buckets ─────────────────────
  const totals = useMemo(() => {
    const t = { decided: 0, undecided: 0, committed: 0, failed: 0 };
    for (const p of status?.perClass ?? []) {
      t.decided   += p.totalDecided;
      t.undecided += p.totalUndecided;
      t.committed += p.totalCommitted;
      t.failed    += p.totalFailed;
    }
    return t;
  }, [status]);

  const selectedClass = useMemo(
    () => classes.find(c => String(c._id) === selectedClassId) ?? null,
    [classes, selectedClassId]
  );

  // ── Render ───────────────────────────────────────────────────────────
  if (mode === 'loading') {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={32} />
        <Skeleton variant="rounded" height={160} sx={{ mt: 2 }} />
        <Skeleton variant="rounded" height={400} sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={() => navigate('/app/settings/sessions')} aria-label="Back to sessions">
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
          Promotion run
        </Typography>
      </Stack>

      {mode === 'setup' && sourceSession && (
        <Box>
          <PromotionSetupStep sourceSession={sourceSession} onRunStarted={handleRunStarted} />
        </Box>
      )}

      {mode === 'closed' && run && (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, mt: 2, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            Run is closed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 520, mx: 'auto' }}>
            {run.status === 'cancelled'
              ? 'This promotion run was cancelled. No enrollments were committed.'
              : `All decisions are committed. ${run.summary.promoted} promoted, ${run.summary.retained} retained, ${run.summary.graduated} graduated, ${run.summary.dropped} dropped.`}
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            <Button variant="outlined" onClick={() => navigate('/app/settings/sessions')}>Back to sessions</Button>
            {targetSession && (
              <Button variant="contained" disableElevation onClick={() => navigate(`/app/settings/sessions/${targetSession._id}`)}>
                Open target session
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {mode === 'dashboard' && run && (
        <>
          <RunDashboardHeader
            run={run}
            sourceSession={sourceSession}
            targetSession={targetSession}
            totalDecided={totals.decided}
            totalUndecided={totals.undecided}
            totalCommitted={totals.committed}
            totalFailed={totals.failed}
            onFinalize={() => setFinalizeOpen(true)}
          />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
            <Box sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0 }}>
              <ClassStatusList
                classes={classes}
                perClass={status?.perClass ?? []}
                selectedClassId={selectedClassId}
                onSelect={setSelectedClassId}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              {selectedClass ? (
                <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight={700}>{selectedClass.name}</Typography>
                    <Typography variant="body2" color="text.secondary">· Decisions</Typography>
                  </Stack>
                  <DecisionsTable
                    key={selectedClass._id}
                    run={run}
                    classId={String(selectedClass._id)}
                    className={selectedClass.name}
                    classes={classes}
                    onMutated={refreshStatus}
                  />
                </Paper>
              ) : (
                <Paper variant="outlined" sx={{ p: 5, borderRadius: 2, textAlign: 'center', borderStyle: 'dashed' }}>
                  <Typography variant="body1" fontWeight={500}>Pick a class on the left</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Each class's decisions, smart defaults, and commit live here.
                  </Typography>
                </Paper>
              )}
            </Box>
          </Stack>

          {totals.failed > 0 && (
            <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
              <AlertTitle sx={{ fontWeight: 600 }}>Some chunks failed</AlertTitle>
              Open the affected class on the left and re-commit — the idempotency check skips already-promoted students automatically.
            </Alert>
          )}

          <FinalizeRunDialog
            open={finalizeOpen}
            runId={run._id}
            remaining={totals.undecided + totals.decided}   // anything still source-side
            // Derive both numbers from the same per-class data the sidebar
            // and header use. `run.progress.totalInScope` is a snapshot from
            // run start and silently drifts as soon as the cohort changes.
            totalInScope={totals.decided + totals.undecided + totals.committed}
            totalCommitted={totals.committed}
            onClose={() => setFinalizeOpen(false)}
            onFinalized={handleFinalized}
          />
        </>
      )}
    </Box>
  );
};

export default PromotionWizardPage;
